import { STORY } from '@constants';
import { TrackEvent } from '@enums';
import { useGetProductAndCompanyNamesQuery } from '@graphql/generated/graphql';
import { useAnalytics } from '@lib/analytics/analyticsContext';
import { useNavigationContext } from '@lib/pimster-stories/contexts/NavigationContext';
import { useProgressContext } from '@lib/pimster-stories/contexts/ProgressContext';
import { useRatingContext } from '@lib/pimster-stories/contexts/RatingContext';
import { Share } from '@lib/share/components/Share';
import { NetworkWithOrWithoutConfig } from '@lib/share/types';
import { getNetworkConfig, getNetworkName } from '@lib/share/utils/network';
import { VStack } from '@lib/uikit';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';
import { FC, useMemo } from 'react';

import { LikeButton } from './LikeButton';

export const EngagementButtons: FC = () => {
    const url = window.location.href;
    const { track } = useAnalytics();
    const { locale, query } = useRouter();
    const { t } = useTranslation(STORY, { keyPrefix: 'share' });
    const { story, storyPages } = useNavigationContext();
    const { currentPageIndex } = useProgressContext();
    const { isLiked } = useRatingContext();
    const { data } = useGetProductAndCompanyNamesQuery({
        variables: {
            productSlug: query.product as string,
            companySlug: query.company as string,
            locale,
        },
    });

    const currentStoryPage = useMemo(
        () => storyPages[currentPageIndex],
        [storyPages, currentPageIndex]
    );
    const shareMessageOptions = useMemo(
        () => ({
            url,
            company: data?.products?.[0]?.Company?.Name,
            product: data?.products?.[0]?.Name,
        }),
        [data?.products, url]
    );

    const handleShareButtonClickTrack = (
        network: NetworkWithOrWithoutConfig
    ) => {
        track(TrackEvent.StoryShareButtonClicked, {
            story_slug: story?.Slug || '',
            story_page_title: currentStoryPage.pageTitle,
            story_page_number: currentPageIndex,
            is_liked_story: isLiked,
            network: {
                name: getNetworkName(network),
                config: getNetworkConfig(network),
            },
        });
    };

    return (
        <VStack spacing='sm'>
            <Share
                withCopyToClipboard
                url={url}
                title={t('title')}
                description={t('description')}
                networks={[
                    { whatsapp: { text: t('message', shareMessageOptions) } },
                    'facebook',
                    { twitter: { text: t('message', shareMessageOptions) } },
                ]}
                onButtonClick={handleShareButtonClickTrack}
            />
            <LikeButton />
        </VStack>
    );
};
